import React from 'react';
import Header from '../components/layout/Header';
import { Shield, Lock, Eye, Mail, Database, Globe, UserCheck, Trash2, Cookie, Bell } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-10 h-10 text-green-400" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            Privacy Policy
          </h1>
        </div>
        
        <div className="space-y-8 text-gray-300">
          <p className="text-sm text-gray-500">Last updated: January 2026</p>
          
          <section className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-4">Our Commitment to Your Privacy</h2>
            <p>
              At CesarM Challenges, we take your privacy seriously. This Privacy Policy explains how we collect, 
              use, store, and protect your personal information when you use our platform. By using our Service, 
              you consent to the practices described in this policy.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              1. Information We Collect
            </h2>
            
            <h3 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Information from Twitch Authentication</h3>
            <p className="mb-4">
              When you log in through Twitch, we receive and store the following information:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Twitch User ID (permanent identifier)</li>
              <li>Twitch username and display name</li>
              <li>Profile picture URL</li>
              <li>Email address (if available through Twitch)</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Information You Provide</h3>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Video submission URLs (Twitch clips, YouTube videos)</li>
              <li>Feedback and suggestions submitted through our platform</li>
              <li>Shipping information (for physical prize fulfillment)</li>
              <li>Payment information (for prize payouts - processed securely through third-party providers)</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-gray-200 mt-4 mb-2">Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address and approximate location</li>
              <li>Browser type and device information</li>
              <li>Pages visited and features used</li>
              <li>Timestamps of account activity</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-400" />
              2. How We Use Your Information
            </h2>
            <p className="mb-4">We use your information for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Account Management:</strong> To create and maintain your account</li>
              <li><strong>Challenge Participation:</strong> To process and display your submissions</li>
              <li><strong>Winner Selection:</strong> To identify and contact challenge winners</li>
              <li><strong>Prize Fulfillment:</strong> To deliver prizes to winners</li>
              <li><strong>Notifications:</strong> To send updates about challenges, winners, and platform news</li>
              <li><strong>Leaderboards:</strong> To display your username and achievements publicly</li>
              <li><strong>Platform Improvement:</strong> To analyze usage patterns and improve our Service</li>
              <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security threats</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-purple-400" />
              3. Data Storage and Security
            </h2>
            <div className="bg-gray-900/50 border border-green-500/20 rounded-lg p-4 space-y-4">
              <p>
                <strong>Secure Storage:</strong> Your data is stored on secure servers with encryption at rest and in transit.
              </p>
              <p>
                <strong>Access Controls:</strong> Only authorized personnel have access to personal data, and access is logged and audited.
              </p>
              <p>
                <strong>Data Retention:</strong> We retain your data for as long as your account is active or as needed to provide services. 
                Deleted accounts have their personal data removed within 30 days, except where retention is required by law.
              </p>
              <p>
                <strong>No Sale of Data:</strong> We never sell, rent, or trade your personal information to third parties for marketing purposes.
              </p>
            </div>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              4. Third-Party Services
            </h2>
            <p className="mb-4">We integrate with the following trusted third-party services:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-purple-400 mb-2">Twitch</h4>
                <p className="text-sm">Authentication and user profile data</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-red-400 mb-2">YouTube</h4>
                <p className="text-sm">Video embedding and playback</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-400 mb-2">Discord</h4>
                <p className="text-sm">Community notifications and announcements</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-blue-400 mb-2">Google Analytics</h4>
                <p className="text-sm">Anonymous usage analytics</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-green-400 mb-2">Microsoft (Email)</h4>
                <p className="text-sm">Email notifications for winners</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-400 mb-2">Google AdSense</h4>
                <p className="text-sm">Advertising (may use cookies)</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              Each third-party service has its own privacy policy. We encourage you to review their policies.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-yellow-400" />
              5. Cookies and Tracking
            </h2>
            <p className="mb-4">We use the following types of cookies:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Required for authentication and basic platform functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site (can be disabled)</li>
              <li><strong>Advertising Cookies:</strong> Used by ad partners to show relevant ads (can be disabled)</li>
            </ul>
            <p className="mt-4">
              You can manage cookie preferences through your browser settings. Note that disabling essential 
              cookies may affect platform functionality.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-400" />
              6. Communications
            </h2>
            <p className="mb-4">We may contact you through:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Email:</strong> Winner notifications, important account updates</li>
              <li><strong>Platform Notifications:</strong> Challenge updates, new features</li>
              <li><strong>Discord:</strong> Community announcements (if you join our server)</li>
            </ul>
            <p className="mt-4">
              You can opt out of non-essential communications through your account settings or by contacting us directly.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              7. Your Rights
            </h2>
            <p className="mb-4">You have the following rights regarding your personal data:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Access</h4>
                <p className="text-sm">Request a copy of your personal data</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Correction</h4>
                <p className="text-sm">Request correction of inaccurate data</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Deletion</h4>
                <p className="text-sm">Request deletion of your account and data</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Portability</h4>
                <p className="text-sm">Request your data in a portable format</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Opt-Out</h4>
                <p className="text-sm">Opt out of marketing and analytics</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-2">Objection</h4>
                <p className="text-sm">Object to certain data processing</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              To exercise these rights, please contact us through the methods listed below.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              8. Account Deletion
            </h2>
            <p className="mb-4">
              You may request deletion of your account at any time by contacting us. Upon deletion:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your personal profile data will be removed within 30 days</li>
              <li>Your submissions will be anonymized (username removed)</li>
              <li>Public leaderboard entries will be anonymized</li>
              <li>Any pending prize claims will be forfeited</li>
              <li>Some data may be retained for legal compliance (fraud prevention, legal disputes)</li>
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">9. Children's Privacy</h2>
            <p>
              CesarM Challenges is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you believe we have collected 
              information from a child under 13, please contact us immediately so we can delete it.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">10. International Users</h2>
            <p>
              If you access our Service from outside the United States, please be aware that your 
              information may be transferred to, stored, and processed in the United States or other 
              countries. By using our Service, you consent to such transfer and processing.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by posting a notice on our Platform or sending an email. Your continued use 
              of the Service after changes take effect constitutes acceptance of the revised policy.
            </p>
          </section>
          
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-400" />
              12. Contact Us
            </h2>
            <p className="mb-4">
              For privacy-related questions, data requests, or concerns, please contact us through:
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://discord.gg/TsqcHNm647" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-600/30 transition-colors"
              >
                Discord Server
              </a>
              <a 
                href="https://x.com/CesarMChallenge" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg text-blue-400 hover:bg-blue-600/30 transition-colors"
              >
                Twitter/X
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              We aim to respond to all privacy-related inquiries within 30 days.
            </p>
          </section>
          
          <div className="border-t border-gray-800 pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              By using CesarM Challenges, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
